import React, { useEffect, useState } from "react";
import Text from "../components/atoms/Text";
import Button from "../components/atoms/Button";
import Icon from "../components/atoms/Icon";
import { useNotification } from "../context/NotificationContext";
import { useUser } from "../context/UserContext";
import {
  isEmailJsConfigured,
  submitContactMessage,
} from "../services/contactService";
import locationIcon from "../assets/location.png";
import nameIcon from "../assets/name.png";
import phoneIcon from "../assets/Phone2.png";

const contactCards = [
  {
    id: "address",
    icon: locationIcon,
    title: "Visit Us",
    lines: ["2715 Ash Dr. San Jose,", "South Dakota 83475"],
  },
  {
    id: "email",
    icon: nameIcon,
    title: "Email Us",
    lines: ["hello@ecobazar.com", "support@ecobazar.com"],
  },
  {
    id: "phone",
    icon: phoneIcon,
    title: "Call Us",
    lines: ["(219) 555-0114", "(164) 333-0487"],
  },
];

const initialFormState = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

export default function Contact() {
  const { notify } = useNotification();
  const { currentUser } = useUser();
  const [formValues, setFormValues] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFormValues((current) => ({
      ...current,
      name: current.name || currentUser?.name || "",
      email: current.email || currentUser?.email || "",
    }));
  }, [currentUser]);

  function updateField(field, value) {
    setFormValues((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function validateForm() {
    const trimmedName = formValues.name.trim();
    const trimmedEmail = formValues.email.trim().toLowerCase();
    const trimmedSubject = formValues.subject.trim();
    const trimmedMessage = formValues.message.trim();

    if (!trimmedName || !trimmedEmail || !trimmedSubject || !trimmedMessage) {
      notify("Please complete all contact form fields.", "error");
      return null;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(trimmedEmail)) {
      notify("Please enter a valid email address.", "error");
      return null;
    }

    return {
      name: trimmedName,
      email: trimmedEmail,
      subject: trimmedSubject,
      message: trimmedMessage,
      userId: currentUser?.id || null,
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const payload = validateForm();

    if (!payload) {
      return;
    }

    setIsSubmitting(true);

    try {
      await submitContactMessage(payload);
      notify("Your message has been sent successfully.");
      setFormValues({
        name: currentUser?.name || "",
        email: currentUser?.email || "",
        subject: "",
        message: "",
      });
    } catch (error) {
      if (!isEmailJsConfigured()) {
        notify(
          "EmailJS is not configured yet. Add your service ID, template ID, and public key to the .env file.",
          "error",
        );
      } else {
        notify("We could not send your message. Please try again.", "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="contact-page">
      <div className="contact-shell">
        <aside className="contact-sidebar-card">
          {contactCards.map((card, index) => (
            <div
              key={card.id}
              className="contact-info-block"
              style={{
                borderBottom:
                  index === contactCards.length - 1
                    ? "none"
                    : "1px solid var(--gray1)",
              }}
            >
              <div className="contact-icon-wrap">
                <Icon
                  src={card.icon}
                  name={card.title}
                  style={{ width: "51px", height: "51px" }}
                />
              </div>
              <div className="contact-info-copy">
                <Text
                  size="px18"
                  weight="w500"
                  color="gray9"
                  extraStyles={{ textAlign: "center" }}
                >
                  {card.title}
                </Text>
                <div className="contact-info-lines">
                  {card.lines.map((line) => (
                    <Text
                      key={line}
                      size="px14"
                      weight="w400"
                      color="gray5"
                      extraStyles={{
                        textAlign: "center",
                        lineHeight: "160%",
                      }}
                    >
                      {line}
                    </Text>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </aside>

        <section className="contact-form-card">
          <div className="contact-form-copy">
            <Text
              size="px32"
              weight="w600"
              color="gray9"
              extraStyles={{ textAlign: "start", marginBottom: "12px" }}
            >
              Just Say Hello!
            </Text>
            <Text
              size="px14"
              weight="w400"
              color="gray5"
              extraStyles={{
                textAlign: "start",
                lineHeight: "170%",
                maxWidth: "560px",
              }}
            >
              Do you have a question, feedback, or a project in mind? Send us a
              message and we&apos;ll get back to you as soon as we can.
            </Text>
          </div>

          <form className="contact-form-grid" onSubmit={handleSubmit}>
            <div className="contact-form-row">
              <input
                type="text"
                placeholder="Your Name"
                value={formValues.name}
                onChange={(event) => updateField("name", event.target.value)}
                className="contact-input"
              />
              <input
                type="email"
                placeholder="Email Address"
                value={formValues.email}
                onChange={(event) => updateField("email", event.target.value)}
                className="contact-input"
              />
            </div>

            <input
              type="text"
              placeholder="Subject"
              value={formValues.subject}
              onChange={(event) => updateField("subject", event.target.value)}
              className="contact-input"
            />

            <textarea
              placeholder="Write your message"
              value={formValues.message}
              onChange={(event) => updateField("message", event.target.value)}
              className="contact-textarea"
            />

            <div className="contact-form-actions">
              <Button
                type="submit"
                size="medium"
                variant="fill"
                disabled={isSubmitting}
                style={{
                  minWidth: "180px",
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? "wait" : "pointer",
                }}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
